export class DeconstructedURL {
	#domain: string;

	get domain(): string {
		return this.#domain;
	}

	set domain(value: string) {
		this.#domain = value;
		this.rebuildHostname();
	}

	#extension: string;

	get extension(): string {
		return this.#extension;
	}

	set extension(value: string) {
		this.#extension = value;
		this.rebuildHostname();
	}

	#host: string;

	get host(): string {
		return this.#host;
	}

	set host(value: string) {
		this.parseHost(value);
	}

	#hostname: string;

	get hostname(): string {
		return this.#hostname;
	}

	set hostname(value: string) {
		this.parseHostname(value);
	}

	#origin: string;

	get origin(): string {
		return this.#origin;
	}

	pathname: string;

	#port: string;

	get port(): string {
		return this.#port;
	}

	set port(value: string) {
		this.#port = value;
		this.rebuildHostname();
	}

	#protocol: string;

	get protocol(): string {
		return this.#protocol;
	}

	set protocol(value: string) {
		this.#protocol = value;
		this.rebuildOrigin();
	}

	get search(): string {
		const value = this.searchParams.toString();

		return value ? `?${value}` : '';
	}

	searchParams: URLSearchParams;

	#subdomain: string;

	get subdomain(): string {
		return this.#subdomain;
	}

	set subdomain(value: string) {
		this.#subdomain = value;
		this.rebuildHostname();
	}

	constructor(url: string | URL) {
		const parsedUrl = typeof url === 'string' ? new URL(url) : url;
		const { host, pathname, protocol, searchParams } = parsedUrl;

		this.pathname = pathname;
		this.protocol = protocol.toLowerCase();
		this.searchParams = searchParams;

		this.parseHost(host.toLowerCase());
	}

	private parseHost(value: string): void {
		let hostParts = value.split('.');

		// Special handling for feature environments (i.e. app.feature1.seesonar.com)
		if (hostParts.length >= 4) {
			const domainPart = hostParts.slice(1, hostParts.length - 1).join('.');
			hostParts = [ hostParts[ 0 ], domainPart, hostParts[ hostParts.length - 1 ] ];
		}

		const isOnlyLocalhost = hostParts.length === 1;
		const hasSubdomain = !isOnlyLocalhost && hostParts.length === 3;

		let subdomain = hasSubdomain ? hostParts[ 0 ] : '';
		let domain = hasSubdomain ? hostParts[ 1 ] : hostParts[ 0 ];
		const extensionAndPort = hasSubdomain ? hostParts[ 2 ] : hostParts[ 1 ];
		let [ extension, port ] = extensionAndPort?.toLowerCase().split(':') ?? [];

		if (extension === 'localhost') {
			subdomain = domain;
			domain = extension;
			extension = '';
		}

		if (domain.includes(':')) {
			[ domain, port ] = domain.split(':');
		}

		this.#domain = domain.toLowerCase();
		this.#extension = extension?.toLowerCase() ?? '';
		this.#port = port ?? '';
		this.#subdomain = subdomain?.toLowerCase() ?? '';

		this.rebuildHostname();
	}

	private parseHostname(value: string): void {
		this.parseHost(this.#port ? `${value}:${this.#port}` : value);
	}

	private rebuildHost(): void {
		this.#host = this.#port ? `${this.#hostname}:${this.#port}` : this.#hostname;

		this.rebuildOrigin();
	}

	private rebuildHostname(): void {
		const hostnameParts = [];

		if (this.#subdomain) {
			hostnameParts.push(this.#subdomain);
		}

		hostnameParts.push(this.#domain);

		if (this.#extension) {
			hostnameParts.push(this.#extension);
		}

		this.#hostname = hostnameParts.join('.');

		this.rebuildHost();
	}

	private rebuildOrigin(): void {
		this.#origin = `${this.#protocol}//${this.#host}`;
	}

	toString(): string {
		const path = this.pathname === '/' ? '' : this.pathname;

		return `${this.#origin}${path}${this.search}`;
	}

	asURL(): URL {
		return new URL(this.toString());
	}
}
